import alerts from '@/config/alerts.json'
import type {Dispatch, ReactElement, SetStateAction} from 'react'
import {Dialog, Transition} from '@headlessui/react'
import {Fragment, useState} from 'react'
import React, {useEffect} from 'react'
import Verifications from './Verifications'
import {AnimatePresence, motion} from 'framer-motion'
import {VerificationData} from '@/types'
import {useMutation} from 'react-query'
import {ResendVerification} from '@/services/api/auth'
import toast from 'react-hot-toast'
import Login from './Login'
import CustomToast from '../CustomToast'

type Props = {
  isOpen: boolean
  setIsRegisterOpen: Dispatch<SetStateAction<boolean>>
  setLastOpened: Dispatch<SetStateAction<'login' | 'register'>>
  setIsForgotOpen: Dispatch<SetStateAction<boolean>>
  setIsLoginOpen: Dispatch<SetStateAction<boolean>>
}

export default function LoginModal({
  isOpen,
  setIsLoginOpen,
  setIsRegisterOpen,
  setIsForgotOpen,
  setLastOpened,
}: Props): ReactElement {
  const closeModal = () => {
    setIsLoginOpen(false)
  }
  const [verificationError, setVerificationError] = React.useState(false)
  const [verificationData, setVerificationData] = React.useState<
    undefined | VerificationData
  >()
  const [shouldVerify, setShouldVerify] = React.useState(false)

  const ResendMutation = useMutation(ResendVerification)

  const handleVerification = () => {
    const phone = verificationData?.phone
    if (verificationData && phone) {
      ResendMutation.mutate(
        {
          country_code: phone.country_code,
          number: phone.number,
        },
        {
          onSuccess() {
            toast.custom((t) => <CustomToast t={t} message={alerts.validation_code} type="success" />, {
              duration: 2500,
            });
          },
          onError(error: any) {
            toast.custom((t) => <CustomToast t={t} message={error.response.data.message} type="error" />, {
              duration: 4000,
              id: 'Validation Error',
            });
          },
        },
      )
    }
  }
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[110]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md xl:max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <Dialog.Title className="text-lg font-medium p-3 lg:px-6 lg:pt-8 xl:px-8 xl:pt-12 2xl:px-11 lg:pb-6 xl:pb-8 2xl:pb-8">
                  <div className="flex  items-end justify-between">
                    <h3 className="text-primary-500 text-right font-bold text-3xl">
                      تسجيل الدخول
                    </h3>
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        setIsLoginOpen(false)
                      }}
                      className="close-icon border bg-gray-200 rounded-md p-2 self-end cursor-pointer">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 21 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M11.9109 10.2326L20.201 1.94261C20.3648 1.75131 20.4504 1.50523 20.4407 1.25355C20.4309 1.00188 20.3266 0.763139 20.1485 0.585044C19.9704 0.40695 19.7317 0.302617 19.48 0.292896C19.2283 0.283175 18.9823 0.368781 18.791 0.532608L10.5009 8.82261L2.21095 0.522607C2.02265 0.334304 1.76725 0.228516 1.50095 0.228516C1.23465 0.228516 0.979253 0.334304 0.79095 0.522607C0.602646 0.710911 0.496858 0.966306 0.496858 1.23261C0.496858 1.49891 0.602646 1.7543 0.79095 1.94261L9.09095 10.2326L0.79095 18.5226C0.686268 18.6123 0.601248 18.7226 0.541224 18.8466C0.481201 18.9707 0.447471 19.1058 0.442151 19.2436C0.436832 19.3813 0.460037 19.5186 0.510312 19.6469C0.560587 19.7753 0.636845 19.8918 0.7343 19.9893C0.831754 20.0867 0.948302 20.163 1.07663 20.2132C1.20495 20.2635 1.34228 20.2867 1.48 20.2814C1.61772 20.2761 1.75285 20.2424 1.87692 20.1823C2.00098 20.1223 2.1113 20.0373 2.20095 19.9326L10.5009 11.6426L18.791 19.9326C18.9823 20.0964 19.2283 20.182 19.48 20.1723C19.7317 20.1626 19.9704 20.0583 20.1485 19.8802C20.3266 19.7021 20.4309 19.4633 20.4407 19.2117C20.4504 18.96 20.3648 18.7139 20.201 18.5226L11.9109 10.2326Z"
                          fill="gray"
                        />
                      </svg>
                    </a>
                  </div>
                </Dialog.Title>
                <div className="modal-content p-3 lg:p-6 xl:p-8 2xl:p-11 pt-0 lg:pt-0 xl:pt-0 2xl:pt-0">
                  <AnimatePresence>
                    <motion.div
                      initial={{opacity: 0, translateX: '10%'}}
                      transition={{type: 'spring', duration: 0.5}}
                      animate={{opacity: 1, translateX: '0'}}
                      exit={{opacity: 0, translateX: '10%'}}>
                      {/*
                       * In Case the User is Not Verified Show this
                       */}
                      {verificationData && shouldVerify ? (
                        <Verifications
                          phone={verificationData.phone ?? ''}
                          fromRegister={false}
                          setShouldVerify={setShouldVerify}
                          setLastOpened={setLastOpened}
                          setVerificationData={setVerificationData}
                          setIsLoginOpen={setIsLoginOpen}
                          setIsRegisterOpen={setIsRegisterOpen}
                        />
                      ) : (
                        <>
                          <AnimatePresence>
                            <motion.div
                              initial={{opacity: 0, translateX: '10%'}}
                              transition={{type: 'spring', duration: 0.5}}
                              animate={{opacity: 1, translateX: '0'}}
                              exit={{opacity: 0, translateX: '10%'}}>
                              <>
                                {verificationError && (
                                  <div
                                    className="bg-red border border-red text-white px-4 py-3 rounded relative mb-3"
                                    role="alert">
                                    <strong className="font-bold">
                                      خطأ التحقق!
                                    </strong>
                                    <span className="block sm:inline mr-3 text-sm">
                                      <span>
                                        <a
                                          href="#"
                                          onClick={e => {
                                            e.preventDefault()
                                            setVerificationError(false)
                                            handleVerification()
                                            setShouldVerify(true)
                                          }}
                                          className="text-primary font-semibold underline">
                                          التحقق من الحساب
                                        </a>
                                      </span>
                                    </span>
                                    <span
                                      onClick={e => {
                                        e.preventDefault()
                                        setVerificationError(false)
                                      }}
                                      className="absolute top-0 bottom-0 right-0 px-4 py-3">
                                      <svg
                                        className="fill-current h-6 w-6 text-red-500"
                                        role="button"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20">
                                        <title>Close</title>
                                        <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                                      </svg>
                                    </span>
                                  </div>
                                )}
                                <Login
                                  setIsLoginOpen={setIsLoginOpen}
                                  setVerificationError={setVerificationError}
                                  setVerificationData={setVerificationData}
                                  setIsForgotOpen={setIsForgotOpen}
                                />

                                <p className="text-center mt-6 text-sm">
                                  ليس لديك حساب؟
                                  <a
                                    href="#"
                                    onClick={() => {
                                      setIsLoginOpen(false)
                                      setTimeout(() => {
                                        setLastOpened('register')
                                        setIsRegisterOpen(true)
                                      }, 500)
                                    }}
                                    data-test="register-page"
                                    className="text-primary cursor-pointer underline font-bold mr-1 mt-4">
                                    سجل حساب جديد
                                  </a>
                                </p>
                              </>
                            </motion.div>
                          </AnimatePresence>
                        </>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
